import { Link, useLocation } from "react-router";
import { Boxes, ChevronLeft, ChevronRight, Palette } from "lucide-react";
import { useState } from "react";
import type { LucideIcon } from "lucide-react";

import logo from "@/assets/isotipo.svg";
import type { UserProfile } from "@/features/account_manager/models/UserProfile";
import { useBrandingTheme } from "@/features/branding/hooks/BrandingProvider";

import financeIcon from "@/assets/icons/finance.svg";
import accountsIcon from "@/assets/icons/accounts.svg";
import plansIcon from "@/assets/icons/plans.svg";
import settingsIcon from "@/assets/icons/settings.svg";
import agentsIcon from "@/assets/icons/board.svg"
import integrationsIcon from "@/assets/icons/integraciones.svg";
import Roles from "@/shared/valueObjects/Roles";

interface MenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  svgIcon?: string;
  path: string;
  badge?: number;
}

const superAdminItems: MenuItem[] = [
  {
    id: "finance",
    label: "Finanzas",
    svgIcon: financeIcon,
    path: "/admin",
  },
  {
    id: "accounts",
    label: "Cuentas",
    svgIcon: accountsIcon,
    path: "/admin/accounts",
  },
  {
    id: "plans",
    label: "Planes",
    svgIcon: plansIcon,
    path: "/admin/plans",
  },
  {
    id: "integrations",
    label: "Integraciones",
    svgIcon: integrationsIcon,
    path: "/admin/integrations",
  },
  {
    id: "automations",
    label: "Automatizaciones",
    svgIcon: agentsIcon,
    path: "/admin/automations",
  },
  {
    id: "workspaces",
    label: "Workspaces",
    icon: Boxes,
    path: "/admin/workspaces",
  },

  {
    id: "settings",
    label: "Configuraciones",
    svgIcon: settingsIcon,
    path: "/admin/settings",
  },
];

const adminItems: MenuItem[] = [
  {
    id: "finance",
    label: "Finanzas",
    svgIcon: financeIcon,
    path: "/admin",
  },
  {
    id: "accounts",
    label: "Cuentas",
    svgIcon: accountsIcon,
    path: "/admin/accounts",
  },
  {
    id: "plans",
    label: "Planes",
    svgIcon: plansIcon,
    path: "/admin/plans",
  },
  {
    id: "settings",
    label: "Ajustes",
    svgIcon: settingsIcon,
    path: "/admin/settings",
  },
  {
    id: "integrations",
    label: "Integraciones",
    svgIcon: integrationsIcon,
    path: "/admin/client_integrations",
  },
  {
    id: "automations",
    label: "Automatizaciones",
    svgIcon: agentsIcon,
    path: "/admin/automations",
  },
  {
    id: "workspaces",
    label: "Workspaces",
    icon: Boxes,
    path: "/admin/workspaces",
  },
  {
    id: "branding",
    label: "Marca",
    icon: Palette,
    path: "/admin/branding",
  },
];

const cuentaItems: MenuItem[] = [
  {
    id: "integrations",
    label: "Integraciones",
    svgIcon: integrationsIcon,
    path: "/admin/client_integrations",
  },
];

export default function Sider({ user }: { user: UserProfile }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { logoUrl } = useBrandingTheme();

  const isActive = (path: string) => {
    return (
      location.pathname === path ||
      (path !== "/admin" && location.pathname.startsWith(path))
    );
  };

  const getMenuItems = () => {
    const role = user?.role;
    if (Roles.isSuperAdmin(role)) return superAdminItems;
    if (Roles.isAdmin(role)) return adminItems;
    return cuentaItems;
  };

  const menuItems = getMenuItems();

  return (
    <aside
      className={`
        ${isCollapsed ? "w-28" : "w-64"} 
        h-screen sticky top-0 transition-all duration-300 ease-in-out
        flex flex-col border-r border-white/5 bg-black text-white/90
      `}
    >
      {/* Header */}
      <div className="flex items-center border-white/5 border-b justify-between p-5 px-6">
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            <Link
              className="w-8 h-8 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
              to={"/"}
            >
              <img
                src={logoUrl ?? logo}
                alt="Logo"
                className={logoUrl ? "max-h-8 object-contain" : "brightness-0 invert"}
              />
            </Link>
          </div>
        )}

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`p-1.5 rounded-full hover:bg-white/5 cursor-pointer text-gray-400 hover:text-white transition-all ${isCollapsed ? "mx-auto" : ""}`}
          aria-label={isCollapsed ? "Expandir menú" : "Colapsar menú"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 py-6">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`
                    flex items-center px-4 py-2.5 rounded-xl transition-all duration-300
                    ${isCollapsed ? "justify-center mx-1" : "justify-start"}
                    ${active
                      ? "bg-white/10 shadow-sm text-white"
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                    }
                  `}
                  title={isCollapsed ? item.label : undefined}
                >
                  {item.svgIcon ? (
                    <img
                      src={item.svgIcon}
                      alt={item.label}
                      className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"} ${active ? "opacity-100" : "opacity-40 grayscale brightness-200"}`}
                      style={{
                        filter: active ? "brightness(0) invert(1)" : "none",
                      }}
                    />
                  ) : (
                    Icon && (
                      <Icon
                        className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"} ${active ? "stroke-[2.5]" : "stroke-[1.8]"}`}
                      />
                    )
                  )}

                  {!isCollapsed && (
                    <div className="flex items-center justify-between w-full">
                      <span className="text-sm font-medium tracking-tight">
                        {item.label}
                      </span>
                      {item.badge && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/5">
        {!isCollapsed && (
          <p className="text-center text-white/10 text-[10px] font-medium tracking-widest uppercase">
            v1.0.0-alpha
          </p>
        )}
      </div>
    </aside>
  );
}
